import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, Truck, Shield, RotateCcw, Award, ChevronDown, ChevronUp, Minus, Plus, RefreshCw, CalendarDays, Bell, X, MapPin, Sparkles, Ruler, Scissors, CircleDot } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getDeliveryWindow, formatShortDate, DELIVERY_TIERS } from '../utils/deliveryEstimate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METAL_COLORS = [
  { key: 'yellow', color: '#FFD700', label: 'Yellow Gold' },
  { key: 'rose', color: '#E8A87C', label: 'Rose Gold' },
  { key: 'white', color: '#E0E0E0', label: 'White Gold' },
];

const KARAT_OPTIONS = [
  { value: '22K', label: '22K', purity: '91.6%' },
  { value: '18K', label: '18K', purity: '75.0%' },
  { value: '14K', label: '14K', purity: '58.3%' },
  { value: '9K', label: '9K', purity: '37.5%' },
];

// Standard Indian ring sizes (1 = 41.4 mm circumference, +1 size = +0.83 mm)
const RING_SIZES = ['6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22'];

// Indian ring size lookup: { size: { mm_diameter, mm_circumference } }
// Source: BIS standard – size 1 = 13.18mm dia / 41.40mm circ, +0.265mm dia per size
const INDIAN_RING_TABLE = RING_SIZES.map((s) => {
  const sizeNum = parseInt(s, 10);
  const dia = +(13.18 + (sizeNum - 1) * 0.265).toFixed(2);
  const circ = +(dia * Math.PI).toFixed(2);
  return { size: s, diameter: dia, circumference: circ };
});

const findSizeFromMm = (mm, key) => {
  const target = parseFloat(mm);
  if (!target || isNaN(target)) return null;
  let best = null;
  for (const row of INDIAN_RING_TABLE) {
    const diff = Math.abs(row[key] - target);
    if (!best || diff < best.diff) best = { ...row, diff };
  }
  return best;
};

const InfoAccordion = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[rgba(45,59,38,0.1)]">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-4 text-left">
        <span className="text-sm font-semibold text-[#2D3B26] uppercase tracking-wider">{title}</span>
        {open ? <ChevronUp size={18} className="text-[#7A8A73]" /> : <ChevronDown size={18} className="text-[#7A8A73]" />}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [liked, setLiked] = useState(false);
  const [selectedMetal, setSelectedMetal] = useState(0);
  const [selectedKarat, setSelectedKarat] = useState('22K');
  const [selectedSize, setSelectedSize] = useState('');
  const [goldRate, setGoldRate] = useState(null);
  const [dynamicPrice, setDynamicPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const [pincode, setPincode] = useState('');
  const [pincodeApplied, setPincodeApplied] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [drawerCart, setDrawerCart] = useState(null);
  const [findSizeOpen, setFindSizeOpen] = useState(false);
  const [findSizeMethod, setFindSizeMethod] = useState('circumference'); // 'circumference' | 'diameter' | 'paper'
  const [findSizeInput, setFindSizeInput] = useState('');

  useEffect(() => {
    fetchProduct();
    fetchGoldRate();
  }, [id]);

  useEffect(() => {
    if (product && product.category === 'gold') {
      calculatePrice();
    }
  }, [selectedKarat, product]);

  const fetchProduct = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products/${id}`, { withCredentials: true });
      setProduct(data);
    } catch (error) { toast.error('Failed to load product'); }
    finally { setLoading(false); }
  };

  const fetchGoldRate = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/gold-rate`, { withCredentials: true });
      setGoldRate(data);
    } catch (error) { console.error('Failed to fetch gold rate'); }
  };

  const calculatePrice = async () => {
    if (!product?.weight_grams || product.weight_grams <= 0) {
      setDynamicPrice(null);
      return;
    }
    setPriceLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/calculate-price/${id}?karat=${selectedKarat}`, { withCredentials: true });
      setDynamicPrice(data);
    } catch (error) { console.error('Price calculation error'); }
    finally { setPriceLoading(false); }
  };

  const addToCart = async () => {
    if (!user) { toast.error('Please login to add items to cart'); navigate('/login'); return; }
    const isRing = product?.jewelry_type === 'ring';
    if (isRing && !selectedSize) {
      toast.error('Please select your ring size first');
      return;
    }
    try {
      const isGold = product?.category === 'gold';
      const payload = {
        product_id: id,
        quantity,
        karat: isGold ? selectedKarat : null,
        size: isRing ? selectedSize : null,
      };
      await axios.post(`${BACKEND_URL}/api/cart`, payload, { withCredentials: true });
      toast.success(`Added to cart${isGold ? ` (${selectedKarat})` : ''}${isRing ? ` · Size ${selectedSize}` : ''}!`);
      // Fetch updated cart for the bottom-sheet preview
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/cart`, { withCredentials: true });
        setDrawerCart(data);
      } catch (_) { /* drawer will fall back to product summary */ }
      setCartDrawerOpen(true);
    } catch (error) { toast.error('Failed to add to cart'); }
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    const trimmed = notifyEmail.trim();
    if (!trimmed.includes('@')) { toast.error('Enter a valid email'); return; }
    setNotifySubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/products/${id}/notify-me`, { email: trimmed });
      toast.success(data.message);
      setNotifyDone(true);
      setNotifyOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not register');
    } finally {
      setNotifySubmitting(false);
    }
  };

  const addToWishlist = async () => {
    if (!user) { toast.error('Please login to add items to wishlist'); navigate('/login'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/wishlist`, { product_id: id }, { withCredentials: true });
      setLiked(true);
      toast.success('Added to wishlist!');
    } catch (error) { toast.error('Failed to add to wishlist'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C27054]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <p className="text-[#7A8A73] text-lg mb-4">Product not found</p>
          <Link to="/products" className="text-[#C27054] hover:underline">Back to products</Link>
        </div>
      </div>
    );
  }

  const metalSwatches = product.category === 'gold' ? METAL_COLORS : METAL_COLORS.slice(2);
  const displayPrice = dynamicPrice?.total_price || product.price;
  const isGold = product.category === 'gold';
  const hasDynamicPricing = isGold && product.weight_grams && product.weight_grams > 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-20 md:pt-28 pb-24 lg:pb-0" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 py-3 border-b border-[rgba(45,59,38,0.06)]">
        <div className="flex items-center gap-2 text-xs text-[#7A8A73]">
          <Link to="/" className="hover:text-[#C27054] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-[#C27054] transition-colors">Jewelry</Link>
          <span>/</span>
          <Link to={`/products?category=${product.category}`} className="hover:text-[#C27054] transition-colors">
            {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
          </Link>
          <span>/</span>
          <span className="text-[#2D3B26] font-medium">{product.name}</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
          {/* LEFT - Product Image Gallery */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <div className="lg:sticky lg:top-32">
              {(() => {
                const images = product.gallery_images && product.gallery_images.length
                  ? product.gallery_images
                  : [product.image_url, product.image_url, product.image_url];

                let lastTap = 0;
                const handleTap = () => {
                  const now = Date.now();
                  if (now - lastTap < 300) setImageZoomed((z) => !z);
                  lastTap = now;
                };

                return (
                  <>
                    {/* Main image — desktop: static; mobile: horizontal swipe carousel */}
                    <div
                      className="relative aspect-square bg-[#fafaf8] rounded-2xl overflow-hidden"
                      data-testid="product-gallery"
                    >
                      {/* Mobile swipeable carousel */}
                      <div
                        className="lg:hidden flex h-full overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
                        onScroll={(e) => {
                          const w = e.currentTarget.clientWidth;
                          const idx = Math.round(e.currentTarget.scrollLeft / w);
                          if (idx !== activeImageIdx) setActiveImageIdx(idx);
                        }}
                        data-testid="mobile-gallery-strip"
                      >
                        {images.map((src, i) => (
                          <div
                            key={i}
                            className="flex-shrink-0 w-full h-full snap-center flex items-center justify-center"
                            onClick={handleTap}
                          >
                            <img
                              src={src}
                              alt={`${product.name} ${i + 1}`}
                              className={`w-full h-full object-contain p-8 transition-transform duration-300 ease-out ${imageZoomed ? 'scale-[1.8]' : 'scale-100'}`}
                              draggable={false}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Desktop static image */}
                      <img
                        src={images[activeImageIdx]}
                        alt={product.name}
                        className="hidden lg:block w-full h-full object-contain p-8"
                        data-testid="product-image"
                      />

                      {/* Zoom hint badge — mobile only */}
                      <div className="lg:hidden absolute bottom-3 right-3 bg-[#2D3B26]/85 text-white text-[10px] font-medium px-3 py-1.5 rounded-full backdrop-blur-md">
                        {imageZoomed ? 'Tap twice to exit zoom' : 'Double-tap to zoom · Swipe →'}
                      </div>

                      {/* Mobile dot indicators */}
                      <div className="lg:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, i) => (
                          <span
                            key={i}
                            className={`h-1.5 rounded-full transition-all duration-300 ${
                              i === activeImageIdx ? 'w-5 bg-[#C27054]' : 'w-1.5 bg-[#2D3B26]/30'
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Thumbnails — desktop only */}
                    <div className="hidden lg:flex gap-3 mt-4">
                      {images.map((src, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActiveImageIdx(i)}
                          data-testid={`gallery-thumb-${i}`}
                          className={`w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer ${
                            i === activeImageIdx ? 'border-[#C27054]' : 'border-transparent hover:border-[#7A8A73]'
                          } transition-colors`}
                        >
                          <img src={src} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </motion.div>

          {/* RIGHT - Product Info */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            {/* Product Name */}
            <h1 className="text-2xl lg:text-3xl font-serif tracking-tight text-[#2D3B26] leading-snug mb-2" data-testid="product-name">
              {product.name}
            </h1>
            <p className="text-sm text-[#7A8A73] mb-5">{product.description}</p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-2">
              <p className="text-3xl font-semibold text-[#2D3B26]" data-testid="product-price">
                {priceLoading ? '...' : `\u20b9${displayPrice.toLocaleString('en-IN')}`}
              </p>
              <p className="text-sm text-[#7A8A73]">(Incl. of all taxes)</p>
            </div>

            {/* Dynamic Price Breakdown */}
            {hasDynamicPricing && dynamicPrice && (
              <div className="bg-[#f6f7f4] rounded-xl p-4 mb-6 text-sm" data-testid="price-breakdown">
                <div className="flex justify-between text-[#475740] mb-1">
                  <span>Gold ({selectedKarat} @ {'\u20b9'}{dynamicPrice.rate_per_gram.toLocaleString('en-IN')}/g)</span>
                  <span>{'\u20b9'}{dynamicPrice.gold_value.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-[#475740] mb-1">
                  <span>Weight</span>
                  <span>{dynamicPrice.weight_grams}g</span>
                </div>
                <div className="flex justify-between text-[#475740] mb-1">
                  <span>Making Charge ({'\u20b9'}{dynamicPrice.making_charge_per_gram}/g x {dynamicPrice.weight_grams}g)</span>
                  <span>{'\u20b9'}{dynamicPrice.making_charges.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-[#475740] mb-1 pt-1 border-t border-dashed border-[rgba(45,59,38,0.12)]" data-testid="price-breakdown-subtotal">
                  <span>Sub-total</span>
                  <span>{'\u20b9'}{(dynamicPrice.gold_value + dynamicPrice.making_charges).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-[#475740] mb-1" data-testid="price-breakdown-gst">
                  <span>GST ({dynamicPrice.gst_percent ?? 3}%)</span>
                  <span>{'\u20b9'}{(dynamicPrice.gst_amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between font-semibold text-[#2D3B26] border-t border-dashed border-[rgba(45,59,38,0.15)] pt-2 mt-2">
                  <span>Total</span>
                  <span>{'\u20b9'}{dynamicPrice.total_price.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            {/* Gold Rate Info */}
            {isGold && goldRate && (
              <div className="flex items-center gap-2 mb-6 text-xs text-[#7A8A73]">
                <RefreshCw size={12} />
                <span>Today's 24K Gold Rate: {'\u20b9'}{goldRate.rate_24k.toLocaleString('en-IN')}/g</span>
                <span className="text-[#C27054]">({new Date(goldRate.updated_at).toLocaleDateString('en-IN')})</span>
              </div>
            )}

            {/* Karat Selector - Only for Gold */}
            {isGold && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-3">Select Karat (Purity)</p>
                <div className="flex gap-2">
                  {KARAT_OPTIONS.map((k) => (
                    <button
                      key={k.value}
                      onClick={() => setSelectedKarat(k.value)}
                      data-testid={`karat-${k.value}`}
                      className={`flex flex-col items-center px-5 py-3 rounded-xl border-2 transition-all duration-200 ${
                        selectedKarat === k.value
                          ? 'border-[#C27054] bg-[#C27054]/5'
                          : 'border-[rgba(45,59,38,0.12)] hover:border-[#C27054]/40'
                      }`}
                    >
                      <span className={`text-base font-bold ${selectedKarat === k.value ? 'text-[#C27054]' : 'text-[#2D3B26]'}`}>
                        {k.label}
                      </span>
                      <span className="text-[10px] text-[#7A8A73] mt-0.5">{k.purity}</span>
                      {goldRate && (
                        <span className="text-[10px] text-[#475740] mt-0.5">
                          {'\u20b9'}{goldRate.rates[k.value]?.toLocaleString('en-IN')}/g
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Ring Size Selector - Only for ring jewelry type */}
            {product.jewelry_type === 'ring' && (
              <div className="mb-6" data-testid="ring-size-selector">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="text-xs font-semibold text-[#2D3B26] uppercase tracking-wider">
                    Select Ring Size <span className="text-[#C27054]">*</span>
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setFindSizeOpen(true); setFindSizeInput(''); }}
                      data-testid="find-my-size-button"
                      className="text-[11px] text-[#2D3B26] hover:text-[#C27054] font-medium uppercase tracking-wider flex items-center gap-1 border border-[#2D3B26]/20 hover:border-[#C27054] rounded-full px-3 py-1.5 transition-colors"
                    >
                      <Sparkles size={12} /> Find My Size
                    </button>
                    <Link
                      to="/size-guide"
                      data-testid="size-guide-link"
                      className="text-[11px] text-[#C27054] hover:underline font-medium uppercase tracking-wider"
                    >
                      Size Guide
                    </Link>
                  </div>
                </div>
                <div className="grid grid-cols-6 sm:grid-cols-9 gap-2">
                  {RING_SIZES.map((sz) => (
                    <button
                      key={sz}
                      type="button"
                      onClick={() => setSelectedSize(sz)}
                      data-testid={`ring-size-${sz}`}
                      className={`py-2.5 rounded-lg border-2 text-sm font-semibold transition-all duration-200 ${
                        selectedSize === sz
                          ? 'border-[#C27054] bg-[#C27054] text-white shadow-[0_4px_12px_rgba(194,112,84,0.3)]'
                          : 'border-[rgba(45,59,38,0.12)] text-[#2D3B26] hover:border-[#C27054]/40 hover:bg-[#C27054]/5'
                      }`}
                    >
                      {sz}
                    </button>
                  ))}
                </div>
                {!selectedSize && (
                  <p className="text-[11px] text-[#7A8A73] mt-2 italic" data-testid="ring-size-hint">
                    Please select your ring size before adding to cart.
                  </p>
                )}
                {selectedSize && (
                  <p className="text-[11px] text-[#475740] mt-2" data-testid="ring-size-selected">
                    Selected: <span className="font-semibold text-[#C27054]">Size {selectedSize}</span> — Indian standard
                  </p>
                )}
              </div>
            )}

            {/* Metal Quality */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-2">Metal & Quality</p>
              <div className="inline-block px-4 py-2 bg-[#E4E8DF] rounded-lg">
                <span className="text-sm font-medium text-[#2D3B26]">
                  {isGold ? `${selectedKarat} Gold` : product.metal_type}
                </span>
              </div>
            </div>

            {/* Metal Color */}
            <div className="mb-8">
              <p className="text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-3">Metal Color</p>
              <div className="flex items-center gap-3">
                {metalSwatches.map((m, i) => (
                  <button key={m.key} onClick={() => setSelectedMetal(i)} data-testid={`metal-swatch-${m.key}`}
                    className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all ${selectedMetal === i ? 'bg-[#E4E8DF] ring-2 ring-[#C27054]' : 'hover:bg-[#f0f2ed]'}`}>
                    <div className="w-8 h-8 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)]" style={{ backgroundColor: m.color }} />
                    <span className="text-[10px] text-[#475740] font-medium">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity + Buttons */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-[rgba(45,59,38,0.15)] rounded-lg">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} data-testid="decrease-quantity"
                  className="w-10 h-10 flex items-center justify-center text-[#7A8A73] hover:text-[#C27054] transition-colors"><Minus size={16} /></button>
                <span className="w-10 text-center text-sm font-medium text-[#2D3B26]" data-testid="quantity-display">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} data-testid="increase-quantity"
                  className="w-10 h-10 flex items-center justify-center text-[#7A8A73] hover:text-[#C27054] transition-colors"><Plus size={16} /></button>
              </div>
            </div>

            {/* Stock / Delivery / Pre-Order Status */}
            {(() => {
              // Out of stock takes priority
              if (product.in_stock === false) {
                return (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 flex items-center gap-3" data-testid="product-out-of-stock">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <Bell size={18} className="text-red-600" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-red-700">Out of Stock</p>
                      <p className="text-sm font-medium text-[#2D3B26]">This piece is currently unavailable</p>
                      <p className="text-[11px] text-[#7A8A73]">Tap "Notify Me" below — we'll email you the moment it's back.</p>
                    </div>
                  </div>
                );
              }
              // If product has a future available_date, show that instead
              const availDate = product.available_date ? new Date(product.available_date) : null;
              const today = new Date(); today.setHours(0,0,0,0);
              const isPreorder = availDate && availDate > today;
              const fmt = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
              if (isPreorder) {
                return (
                  <div className="bg-[#fff5f0] border border-[#C27054]/30 rounded-xl p-4 mb-5 flex items-center gap-3" data-testid="product-availability">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <CalendarDays size={18} className="text-[#C27054]" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-[#C27054]">Pre-Order · Coming Soon</p>
                      <p className="text-sm font-medium text-[#2D3B26]">Available from {fmt(availDate)}</p>
                      <p className="text-[11px] text-[#7A8A73]">Reserve yours now — ships on launch day.</p>
                    </div>
                  </div>
                );
              }
              const window = getDeliveryWindow(pincodeApplied ? pincode : null);
              return (
                <div className="bg-[#E4E8DF] rounded-xl p-4 mb-5 space-y-3" data-testid="product-delivery-estimate">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <CalendarDays size={18} className="text-[#C27054]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-[#7A8A73]">Estimated Delivery</p>
                      <p className="text-sm font-medium text-[#2D3B26]" data-testid="delivery-window-text">
                        {formatShortDate(window.minDate)} — {formatShortDate(window.maxDate)}
                        <span className="text-[11px] text-[#7A8A73] ml-2">· {window.tier.label} ({window.tier.minDays}–{window.tier.maxDays} working days)</span>
                      </p>
                      <p className="text-[11px] text-[#7A8A73]">Free shipping · Order before 6 PM for fastest dispatch</p>
                    </div>
                  </div>

                  {/* Pincode checker */}
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3B26]/10">
                    <MapPin size={14} className="text-[#7A8A73] flex-shrink-0" />
                    <input
                      type="text"
                      value={pincode}
                      onChange={(e) => { setPincode(e.target.value.replace(/\D/g, '').slice(0, 6)); setPincodeApplied(false); }}
                      placeholder="Enter pincode for exact delivery"
                      data-testid="delivery-pincode-input"
                      className="flex-1 bg-transparent text-xs text-[#2D3B26] placeholder:text-[#7A8A73] focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setPincodeApplied(pincode.length === 6)}
                      disabled={pincode.length !== 6}
                      data-testid="delivery-pincode-check"
                      className="text-[10px] uppercase tracking-wider font-semibold text-[#C27054] hover:text-[#A85B42] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Check
                    </button>
                  </div>

                  {/* Tier reference */}
                  <div className="text-[10px] text-[#7A8A73] grid grid-cols-3 gap-2 pt-1 border-t border-[#2D3B26]/10" data-testid="delivery-tier-reference">
                    <span><span className="font-semibold text-[#475740]">Metro</span> · {DELIVERY_TIERS.metro.minDays}–{DELIVERY_TIERS.metro.maxDays} days</span>
                    <span><span className="font-semibold text-[#475740]">Tier-2/3</span> · {DELIVERY_TIERS.tier2.minDays}–{DELIVERY_TIERS.tier2.maxDays} days</span>
                    <span><span className="font-semibold text-[#475740]">Remote</span> · {DELIVERY_TIERS.remote.minDays}–{DELIVERY_TIERS.remote.maxDays} days</span>
                  </div>
                </div>
              );
            })()}

            <div className="flex gap-3 mb-8">
              {product.in_stock === false ? (
                <button
                  onClick={() => { setNotifyEmail(user?.email || ''); setNotifyOpen(true); }}
                  data-testid="notify-me-button"
                  className="flex-1 bg-[#2D3B26] text-white rounded-lg px-8 py-4 hover:bg-[#1a2a1a] transition-all duration-300 shadow-[0_4px_14px_0_rgba(45,59,38,0.3)] text-sm uppercase tracking-widest font-semibold flex items-center justify-center gap-2"
                >
                  <Bell size={18} /> Notify Me When Available
                </button>
              ) : (
                <button onClick={addToCart} data-testid="add-to-cart-button"
                  className="flex-1 bg-[#C27054] text-white rounded-lg px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.3)] text-sm uppercase tracking-widest font-semibold flex items-center justify-center gap-2">
                  <ShoppingCart size={18} /> Add to Cart
                </button>
              )}
              <button onClick={addToWishlist} data-testid="add-to-wishlist-button"
                className={`w-14 h-14 rounded-lg border flex items-center justify-center transition-all duration-300 ${liked ? 'bg-[#C27054] border-[#C27054] text-white' : 'border-[rgba(45,59,38,0.15)] text-[#7A8A73] hover:border-[#C27054] hover:text-[#C27054]'}`}>
                <Heart size={20} className={liked ? 'fill-white' : ''} />
              </button>
            </div>

            {/* Notify Me Modal */}
            {notifyOpen && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" data-testid="notify-modal" onClick={() => setNotifyOpen(false)}>
                <motion.div
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-2xl p-6 max-w-md w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Bell size={16} className="text-[#C27054]" />
                        <p className="text-xs uppercase tracking-widest font-bold text-[#C27054]">Back-In-Stock Alert</p>
                      </div>
                      <h3 className="text-xl font-serif text-[#2D3B26]">Be the first to know</h3>
                    </div>
                    <button onClick={() => setNotifyOpen(false)} data-testid="notify-close" className="text-[#7A8A73] hover:text-[#2D3B26]">
                      <X size={20} />
                    </button>
                  </div>
                  <p className="text-sm text-[#475740] mb-4">
                    Drop your email and we'll ping you the moment <span className="font-semibold text-[#2D3B26]">{product.name}</span> is back in stock — no spam, just one notification.
                  </p>
                  <form onSubmit={submitNotify} className="space-y-3">
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      data-testid="notify-email-input"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 bg-[#E4E8DF] rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                    />
                    <button
                      type="submit"
                      disabled={notifySubmitting}
                      data-testid="notify-submit-button"
                      className="w-full bg-[#C27054] text-white rounded-xl px-6 py-3 hover:bg-[#A85B42] transition-colors text-sm uppercase tracking-widest font-semibold disabled:opacity-60"
                    >
                      {notifySubmitting ? 'Submitting…' : 'Notify Me'}
                    </button>
                  </form>
                </motion.div>
              </div>
            )}

            {notifyDone && product.in_stock === false && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 text-xs text-green-800 flex items-center gap-2" data-testid="notify-confirmation">
                <Bell size={14} className="text-green-700" />
                You're on the list! We'll email you the moment this is back.
              </div>
            )}

            {/* Trust Features */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: Truck, text: 'Free Delivery' },
                { icon: RotateCcw, text: '30 Days Return' },
                { icon: Shield, text: 'Lifetime Warranty' },
                { icon: Award, text: 'Hallmark Certified' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2.5 px-3 py-2.5 bg-[#f6f7f4] rounded-lg">
                  <Icon size={16} className="text-[#C27054] flex-shrink-0" />
                  <span className="text-xs font-medium text-[#475740]">{text}</span>
                </div>
              ))}
            </div>

            {/* Accordions */}
            <div className="border-t border-[rgba(45,59,38,0.1)]">
              <InfoAccordion title="Product Details" defaultOpen={true}>
                <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                  <div>
                    <p className="text-xs text-[#7A8A73] mb-0.5">Product Type</p>
                    <p className="text-sm font-medium text-[#2D3B26]">{product.jewelry_type ? product.jewelry_type.charAt(0).toUpperCase() + product.jewelry_type.slice(1) : product.category.charAt(0).toUpperCase() + product.category.slice(1)} Jewelry</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#7A8A73] mb-0.5">Category</p>
                    <p className="text-sm font-medium text-[#2D3B26]">{product.category.charAt(0).toUpperCase() + product.category.slice(1)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#7A8A73] mb-0.5">Metal Purity</p>
                    <p className="text-sm font-medium text-[#2D3B26]">{isGold ? selectedKarat : product.metal_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#7A8A73] mb-0.5">Metal Color</p>
                    <p className="text-sm font-medium text-[#2D3B26]">{metalSwatches[selectedMetal]?.label || 'Yellow Gold'}</p>
                  </div>
                  {product.weight && (
                    <div>
                      <p className="text-xs text-[#7A8A73] mb-0.5">Metal Weight</p>
                      <p className="text-sm font-medium text-[#2D3B26]">{product.weight}</p>
                    </div>
                  )}
                  {product.size_guide && (
                    <div>
                      <p className="text-xs text-[#7A8A73] mb-0.5">Size</p>
                      <p className="text-sm font-medium text-[#2D3B26]">{product.size_guide}</p>
                    </div>
                  )}
                </div>
              </InfoAccordion>

              <InfoAccordion title="Metal Information">
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-dashed border-[rgba(45,59,38,0.08)]">
                    <span className="text-sm text-[#7A8A73]">Metal</span>
                    <span className="text-sm font-medium text-[#2D3B26]">{product.category === 'gold' ? 'Gold' : 'Silver'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-dashed border-[rgba(45,59,38,0.08)]">
                    <span className="text-sm text-[#7A8A73]">Purity</span>
                    <span className="text-sm font-medium text-[#2D3B26]">{isGold ? `${selectedKarat} (${KARAT_OPTIONS.find(k => k.value === selectedKarat)?.purity})` : product.metal_type}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-dashed border-[rgba(45,59,38,0.08)]">
                    <span className="text-sm text-[#7A8A73]">Color</span>
                    <span className="text-sm font-medium text-[#2D3B26]">{metalSwatches[selectedMetal]?.label || 'Yellow Gold'}</span>
                  </div>
                  {product.weight && (
                    <div className="flex justify-between py-2 border-b border-dashed border-[rgba(45,59,38,0.08)]">
                      <span className="text-sm text-[#7A8A73]">Weight (Approx.)</span>
                      <span className="text-sm font-medium text-[#2D3B26]">{product.weight}</span>
                    </div>
                  )}
                  {isGold && goldRate && (
                    <div className="flex justify-between py-2 border-b border-dashed border-[rgba(45,59,38,0.08)]">
                      <span className="text-sm text-[#7A8A73]">Today's {selectedKarat} Rate</span>
                      <span className="text-sm font-medium text-[#C27054]">{'\u20b9'}{goldRate.rates[selectedKarat]?.toLocaleString('en-IN')}/g</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2">
                    <span className="text-sm text-[#7A8A73]">Hallmark</span>
                    <span className="text-sm font-medium text-[#2D3B26]">BIS Certified</span>
                  </div>
                </div>
              </InfoAccordion>

              <InfoAccordion title="Size Guide">
                <div className="space-y-3">
                  {product.size_guide && <p className="text-sm text-[#475740]">{product.size_guide}</p>}
                  <Link to="/size-guide" className="inline-flex items-center gap-1.5 text-sm text-[#C27054] hover:underline font-medium">View Full Size Guide</Link>
                </div>
              </InfoAccordion>

              <InfoAccordion title="Shipping & Returns">
                <div className="space-y-3">
                  <Link to="/policies#free-shipping" className="flex gap-3 hover:bg-[#fafaf8] -mx-2 px-2 py-1 rounded-lg transition-colors"><Truck size={18} className="text-[#C27054] flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-[#2D3B26]">Free Delivery</p><p className="text-xs text-[#7A8A73]">Free shipping on every order. Insured, tracked, dispatched within 24 hours.</p></div></Link>
                  <Link to="/policies#returns" className="flex gap-3 hover:bg-[#fafaf8] -mx-2 px-2 py-1 rounded-lg transition-colors"><RotateCcw size={18} className="text-[#C27054] flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-[#2D3B26]">30-Day Returns</p><p className="text-xs text-[#7A8A73]">After quality &amp; weight verification, we refund 100% of the invoice value.</p></div></Link>
                  <Link to="/policies#warranty" className="flex gap-3 hover:bg-[#fafaf8] -mx-2 px-2 py-1 rounded-lg transition-colors"><Shield size={18} className="text-[#C27054] flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-[#2D3B26]">Lifetime Warranty</p><p className="text-xs text-[#7A8A73]">If your piece gets damaged anywhere, anytime — we repair it for life.</p></div></Link>
                  <Link to="/policies#certified" className="flex gap-3 hover:bg-[#fafaf8] -mx-2 px-2 py-1 rounded-lg transition-colors"><Award size={18} className="text-[#C27054] flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-[#2D3B26]">BIS Hallmark Certified</p><p className="text-xs text-[#7A8A73]">Government-verified purity. Every piece carries a unique HUID.</p></div></Link>
                </div>
              </InfoAccordion>
            </div>
          </motion.div>
        </div>
      </div>

      {/* MOBILE STICKY ADD-TO-CART BAR */}
      <div
        className="lg:hidden fixed left-0 right-0 z-30 bg-[#FDFBF7]/97 backdrop-blur-xl border-t border-[#2D3B26]/10 shadow-[0_-4px_20px_rgba(45,59,38,0.08)]"
        style={{ bottom: 'calc(64px + env(safe-area-inset-bottom))' }}
        data-testid="sticky-add-to-cart-bar"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#fafaf8] flex-shrink-0">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-contain p-1" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-[#2D3B26] truncate" data-testid="sticky-product-name">{product.name}</p>
            <p className="text-[15px] font-semibold text-[#C27054]" data-testid="sticky-product-price">
              {priceLoading ? '...' : `\u20b9${displayPrice.toLocaleString('en-IN')}`}
            </p>
          </div>
          {product.in_stock === false ? (
            <button
              onClick={() => { setNotifyEmail(user?.email || ''); setNotifyOpen(true); }}
              data-testid="sticky-notify-button"
              className="bg-[#2D3B26] text-white rounded-full px-5 py-3 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 active:scale-95 transition-transform"
            >
              <Bell size={14} /> Notify
            </button>
          ) : (
            <button
              onClick={addToCart}
              data-testid="sticky-add-to-cart-button"
              className="bg-[#C27054] text-white rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 shadow-[0_4px_14px_0_rgba(194,112,84,0.4)] active:scale-95 transition-transform"
            >
              <ShoppingCart size={14} /> Add to Cart
            </button>
          )}
        </div>
      </div>

      {/* BOTTOM SHEET CART PREVIEW */}
      {cartDrawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartDrawerOpen(false)}
            className="fixed inset-0 z-50 bg-[#2D3B26]/50 backdrop-blur-[2px]"
            data-testid="cart-drawer-backdrop"
          />
          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-[#FDFBF7] rounded-t-[28px] shadow-[0_-12px_40px_rgba(45,59,38,0.18)] max-h-[85vh] overflow-hidden flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            data-testid="cart-drawer"
          >
            {/* Drag handle */}
            <div className="pt-3 pb-1 flex justify-center">
              <div className="w-10 h-1 bg-[#2D3B26]/15 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-6 pt-3 pb-4 flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#7A8A73] mb-1 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Added to Cart
                </p>
                <h3 className="text-lg font-serif text-[#2D3B26]">{product.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setCartDrawerOpen(false)}
                data-testid="cart-drawer-close"
                className="p-1.5 rounded-full hover:bg-[#2D3B26]/5"
              >
                <X size={20} className="text-[#2D3B26]" />
              </button>
            </div>

            {/* Cart Summary */}
            <div className="flex-1 overflow-y-auto px-6">
              <div className="bg-[#fafaf8] rounded-2xl p-4 flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-white flex-shrink-0">
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-contain p-1" />
                </div>
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-[#2D3B26]">{product.name}</p>
                  <p className="text-[11px] text-[#7A8A73]">Qty: {quantity}{product.category === 'gold' ? ` · ${selectedKarat}` : ''}{product.jewelry_type === 'ring' && selectedSize ? ` · Size ${selectedSize}` : ''}</p>
                </div>
                <p className="text-sm font-semibold text-[#C27054]">
                  {`\u20b9${displayPrice.toLocaleString('en-IN')}`}
                </p>
              </div>

              {drawerCart && (
                <div className="space-y-2 text-sm border-t border-dashed border-[#2D3B26]/15 pt-3">
                  <div className="flex justify-between">
                    <span className="text-[#475740]">Items in Cart</span>
                    <span className="font-medium text-[#2D3B26]" data-testid="drawer-items-count">
                      {drawerCart.items?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#475740]">Cart Subtotal</span>
                    <span className="font-medium text-[#2D3B26]" data-testid="drawer-subtotal">
                      ₹{(drawerCart.discounted_subtotal ?? drawerCart.subtotal ?? 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky CTAs */}
            <div className="px-6 py-4 border-t border-[#2D3B26]/10 bg-[#FDFBF7] flex gap-3">
              <button
                type="button"
                onClick={() => setCartDrawerOpen(false)}
                data-testid="drawer-continue-shopping"
                className="flex-1 border border-[#2D3B26]/20 text-[#2D3B26] rounded-full px-4 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#2D3B26]/5"
              >
                Keep Browsing
              </button>
              <Link
                to="/cart"
                data-testid="drawer-view-cart"
                className="flex-1 bg-[#C27054] text-white rounded-full px-4 py-3 text-xs font-semibold uppercase tracking-widest text-center shadow-[0_4px_14px_0_rgba(194,112,84,0.4)] flex items-center justify-center gap-1.5"
              >
                View Cart <ShoppingCart size={14} />
              </Link>
            </div>
          </motion.div>
        </>
      )}

      {/* FIND MY SIZE MODAL */}
      {findSizeOpen && (() => {
        const result = (findSizeMethod === 'circumference' || findSizeMethod === 'diameter')
          ? findSizeFromMm(findSizeInput, findSizeMethod)
          : null;
        return (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFindSizeOpen(false)}
              className="fixed inset-0 z-[70] bg-[#2D3B26]/60 backdrop-blur-[2px]"
              data-testid="find-size-backdrop"
            />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', damping: 24, stiffness: 260 }}
              className="fixed inset-x-4 top-1/2 -translate-y-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:translate-y-0 lg:top-[10vh] lg:inset-x-auto lg:w-[520px] z-[71] bg-[#FDFBF7] rounded-3xl shadow-[0_20px_60px_rgba(45,59,38,0.25)] max-h-[80vh] overflow-y-auto"
              data-testid="find-size-modal"
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#FDFBF7] flex items-start justify-between px-6 pt-6 pb-4 border-b border-[#2D3B26]/10 z-10">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C27054] mb-1 flex items-center gap-1.5">
                    <Sparkles size={12} /> Ring Size Finder
                  </p>
                  <h3 className="text-xl font-serif text-[#2D3B26]">Find Your Perfect Fit</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setFindSizeOpen(false)}
                  data-testid="find-size-close"
                  className="p-1.5 rounded-full hover:bg-[#2D3B26]/5"
                >
                  <X size={20} className="text-[#2D3B26]" />
                </button>
              </div>

              {/* Method Tabs */}
              <div className="px-6 pt-4">
                <div className="grid grid-cols-3 gap-2 bg-[#E4E8DF]/40 p-1 rounded-2xl text-[11px]">
                  {[
                    { key: 'circumference', label: 'Thread', icon: Ruler },
                    { key: 'diameter', label: 'Existing Ring', icon: CircleDot },
                    { key: 'paper', label: 'Paper Strip', icon: Scissors },
                  ].map((m) => {
                    const Icon = m.icon;
                    const active = findSizeMethod === m.key;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => { setFindSizeMethod(m.key); setFindSizeInput(''); }}
                        data-testid={`find-size-tab-${m.key}`}
                        className={`flex flex-col items-center gap-1 py-2.5 rounded-xl transition-colors ${
                          active ? 'bg-white text-[#C27054] shadow-sm font-semibold' : 'text-[#475740] hover:text-[#2D3B26]'
                        }`}
                      >
                        <Icon size={14} />
                        <span className="uppercase tracking-wider">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-6 space-y-4">
                {findSizeMethod === 'circumference' && (
                  <>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-[#2D3B26]">Wrap a thread around your finger.</p>
                      <ol className="text-[13px] text-[#475740] leading-relaxed space-y-1.5 list-decimal list-inside">
                        <li>Wrap a non-stretchy thread around the base of your finger.</li>
                        <li>Mark the meeting point with a pen.</li>
                        <li>Measure the marked length on a ruler in <strong>mm</strong>.</li>
                        <li>Enter the value below to see your ring size.</li>
                      </ol>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-1.5">Finger Circumference (mm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={findSizeInput}
                        onChange={(e) => setFindSizeInput(e.target.value)}
                        placeholder="e.g. 52.5"
                        data-testid="find-size-circumference-input"
                        className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      />
                    </div>
                  </>
                )}

                {findSizeMethod === 'diameter' && (
                  <>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-[#2D3B26]">Measure a ring you already wear.</p>
                      <ol className="text-[13px] text-[#475740] leading-relaxed space-y-1.5 list-decimal list-inside">
                        <li>Pick a ring that fits the intended finger comfortably.</li>
                        <li>Measure the <strong>inner diameter</strong> in mm with a ruler.</li>
                        <li>Enter the value below.</li>
                      </ol>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-1.5">Inner Diameter (mm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={findSizeInput}
                        onChange={(e) => setFindSizeInput(e.target.value)}
                        placeholder="e.g. 17.3"
                        data-testid="find-size-diameter-input"
                        className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      />
                    </div>
                  </>
                )}

                {findSizeMethod === 'paper' && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-[#2D3B26]">No thread? Use a paper strip.</p>
                    <ol className="text-[13px] text-[#475740] leading-relaxed space-y-2 list-decimal list-inside">
                      <li>Cut a thin paper strip about <strong>10 cm long</strong> and <strong>5 mm wide</strong>.</li>
                      <li>Wrap it around your finger&rsquo;s base — snug but not tight.</li>
                      <li>Mark where the strip overlaps with a pen.</li>
                      <li>Open the strip and measure the marked length in mm.</li>
                      <li>Switch to the <strong>Thread</strong> tab and enter the value.</li>
                    </ol>
                    <div className="bg-[#fff5f0] border border-[#C27054]/20 rounded-xl p-3 text-[12px] text-[#475740]">
                      💡 <strong>Pro tip:</strong> Measure at the end of the day when fingers are slightly larger. Avoid measuring when cold.
                    </div>
                  </div>
                )}

                {/* Result */}
                {result && parseFloat(findSizeInput) > 0 && (
                  <div className="bg-[#2D3B26] text-white rounded-2xl p-5 text-center" data-testid="find-size-result">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-[#C27054] mb-2">Closest Match</p>
                    <p className="text-5xl font-serif mb-1">Size {result.size}</p>
                    <p className="text-xs text-[#E4E8DF]">
                      Indian standard · Diameter {result.diameter}mm · Circumference {result.circumference}mm
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSize(result.size);
                        setFindSizeOpen(false);
                        toast.success(`Ring size ${result.size} selected`);
                      }}
                      data-testid="find-size-apply"
                      className="mt-4 bg-[#C27054] text-white rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#A85B42] transition-colors"
                    >
                      Use Size {result.size}
                    </button>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 pb-6 text-center">
                <p className="text-[11px] text-[#7A8A73] italic">
                  Need help? Tap the WhatsApp button to chat with a NOVA size expert.
                </p>
              </div>
            </motion.div>
          </>
        );
      })()}
    </div>
  );
};

export default ProductDetail;
